//******************************************************************/
// defines 4 toolbars on viewport
// view navigation toolbar     - top right horizontal
// selection actions toolbar   - top right vertical
// content tools toolbar       - top left vertical
// home toolbar                - top left horizontal
//******************************************************************/

import { IModelApp, ScreenViewport } from "@itwin/core-frontend";
import { LookAndMoveTool, SetupWalkCameraTool, ViewClipClearTool } from "@itwin/core-frontend";
import { StageUsage, ToolbarOrientation, ToolbarUsage,  UiItemsProvider } from "@itwin/appui-abstract";
import { ActionButton, CommonToolbarItem, ToolbarItemUtilities } from "@itwin/appui-abstract";
import { ConditionalBooleanValue, ConditionalStringValue } from "@itwin/appui-abstract";
import { SyncUiEventId, UiFramework, UiShowHideManager } from "@itwin/appui-react";
import { CoreTools, ToolbarHelper } from "@itwin/appui-react";
import { Range3d } from "@itwin/core-geometry";

import { cView } from "./View";
import { cViewClip } from "./ViewClip";
import { cSelection } from "./Selection";
import { cLabel } from "./Label";
import { cRealityData } from "./RealityData";
import { cMessage } from "./Message";
import { cImage } from "./Image";
import { cSlider } from "./Slider";
import { cFinder } from "./Finder";
import { cRMSelect } from "./RMSelector";
import { cHome } from "./Home";
import { cCrossProbing } from "./CrossProbing";
import { cMultiViewport } from "./MultiViewport";

import iconGeminai from "./icons/geminai_logo_512.svg?sprite";
import iconOneWindow from "./icons/oneWindow_logo_512.svg?sprite";
import iconTwoVWindow from "./icons/twoVWindow_logo_512.svg?sprite";
import iconTwoHWindow from "./icons/twoHWindow_logo_512.svg?sprite";
import iconThreeWindow from "./icons/threeWindow_logo_512.svg?sprite";
import iconFourWindow from "./icons/fourWindow_logo_512.svg?sprite";

//******************************************************************/
// toolbar item provider
//******************************************************************/
export class cToolbars implements UiItemsProvider {
  public readonly id = "appToolbars";

  public static setToolbarStyle() {
    UiShowHideManager.useProximityOpacity = false;
    UiFramework.setWidgetOpacity(1.0);
  }

  public provideToolbarButtonItems(
    _stageId: string,
    stageUsage: string,
    toolbarUsage: ToolbarUsage,
    toolbarOrientation: ToolbarOrientation
  ): CommonToolbarItem[] {
    //******************************************************************/
    // view navigation toolbar - top right horizontal
    //******************************************************************/
    if (
      stageUsage === StageUsage.General &&
      toolbarUsage === ToolbarUsage.ViewNavigation &&
      toolbarOrientation === ToolbarOrientation.Horizontal
    ) {
      return tbNavigation.provideToolbarButtonItems();
    }

    //******************************************************************/
    // selection actions toolbar - top right vertical
    //******************************************************************/
    if (
      stageUsage === StageUsage.General &&
      toolbarUsage === ToolbarUsage.ViewNavigation &&
      toolbarOrientation === ToolbarOrientation.Vertical
    ) {
      return tbSelection.provideToolbarButtonItems();
    }

    //******************************************************************/
    // content tools toolbar - top left vertical
    //******************************************************************/
    if (
      stageUsage === StageUsage.General &&
      toolbarUsage === ToolbarUsage.ContentManipulation &&
      toolbarOrientation === ToolbarOrientation.Horizontal
    ) {
      return tbTools.provideToolbarButtonItems();
    }

    //******************************************************************/
    // home tools toolbar - top left corner item
    //******************************************************************/
    if (
      stageUsage === StageUsage.General &&
      toolbarUsage === ToolbarUsage.ContentManipulation &&
      toolbarOrientation === ToolbarOrientation.Vertical
    ) {
      return tbHome.provideToolbarButtonItems();
    }

    return [];
  }
}

//******************************************************************/
// tbNavigation: view navigation toolbar - top right horizontal
//******************************************************************/
class tbNavigation {
  //******************************************************************/
  // callbacks - view navigation toolbar
  //******************************************************************/
  private static zoomOut = async () => {
    IModelApp.viewManager.selectedView?.zoom(undefined, 1.5, {
      animateFrustumChange: true,
    });
  };

  private static zoomIn = async () => {
    IModelApp.viewManager.selectedView?.zoom(undefined, 0.75, {
      animateFrustumChange: true,
    });
  };

  private static setupCamera = async () => {

    // const lookAndMove = new LookAndMoveTool(IModelApp.viewManager.selectedView as ScreenViewport, true);
    // IModelApp.tools.run(lookAndMove.toolId);
    IModelApp.tools.run(SetupWalkCameraTool.toolId);
  };

  //******************************************************************/
  // buttons - view navigation toolbar
  //******************************************************************/
  public static provideToolbarButtonItems(): CommonToolbarItem[] {
    const navToolbar: CommonToolbarItem[] = [
      ToolbarHelper.createToolbarItemFromItemDef(
        10,
        CoreTools.rotateViewCommand
      ),
      ToolbarHelper.createToolbarItemFromItemDef(20, CoreTools.panViewCommand),
      ToolbarHelper.createToolbarItemFromItemDef(30, CoreTools.fitViewCommand),
      ToolbarHelper.createToolbarItemFromItemDef(
        40,
        CoreTools.windowAreaCommand
      ),
      //        ToolbarItemUtilities.createActionButton("zoomOut", 50, "icon-zoom-out", "Zoom Out", cToolbars.zoomOut),
      //        ToolbarItemUtilities.createActionButton("zoomIn", 60, "icon-zoom-in", "Zoom In", cToolbars.zoomIn),
      ToolbarItemUtilities.createActionButton(
        "setupCamera",
        70,
        "icon-camera-eye",
        "Set up Walk Camera",
        tbNavigation.setupCamera
      ),
      ToolbarHelper.createToolbarItemFromItemDef(80, CoreTools.walkViewCommand),
      ToolbarHelper.createToolbarItemFromItemDef(90, CoreTools.viewUndoCommand),
      ToolbarHelper.createToolbarItemFromItemDef(
        100,
        CoreTools.viewRedoCommand
      ),
    ];
    return navToolbar;
  }
}
//******************************************************************/

//******************************************************************/
// tbSelection: selection actions toolbar - top right vertical
//******************************************************************/
class tbSelection {
  private static _clipBox = false;
  private static _clipSel = false;
  private static _clipFactor = 0;
  private static _labelSel = false;
  //  private static _isSelected = false;
  private static _range: Range3d;

  private static _maskTool = false;
  private static _crossProbing = false;

  //******************************************************************/
  // callbacks - selection actions toolbar
  //******************************************************************/
  private static clearSelection = async () => {
    cSelection.clearSelection();
  };

  private static labelSelection() {
    tbSelection._labelSel = tbSelection._labelSel ? false : true;
    cLabel.showLabel(tbSelection._labelSel);

    cMessage.raiseUiEvent("show-sel-label");
  }

  private static gotoSelection = async () => {
    if (cSelection.getSelectionCount() > 0) {
      await cView.zoomToSelection();
    } else {
      cMessage.showStatusBarMessage(
        "Select one or more objects to zoom",
        "icon-cursor-click"
      );
    }
  };

  private static onPopupClip() {
    if (tbSelection._clipBox) {
      tbSelection._range = cViewClip.getClipRange();
      cViewClip.stopClippingTool();
      tbSelection._clipBox = false;
      tbSelection._clipSel = true;
      tbSelection._clipFactor = 0;
    }

    if (!tbSelection._clipSel) {
      tbSelection.clipSelection();
      tbSelection._clipFactor = 0;
    }

    return tbSelection._clipFactor * 10;
  }

  private static onCloseClip() {
    cMessage.clearStatusBarMessage();
  }

  private static clipSelection = async () => {
    tbSelection._range = await cViewClip.clipToSelection();
    if (!tbSelection._range || tbSelection._range.isNull) {
      tbSelection._range = cViewClip.getClipRange();
    }

    if (tbSelection._range && !tbSelection._range.isNull) {
      tbSelection._clipSel = true;
      cMessage.showStatusBarMessage(
        "Use Slider to change clipping region",
        "icon-window-expand"
      );
    } else {
      cMessage.showStatusBarMessage(
        "Select one or more objects to clip",
        "icon-cursor-click"
      );
    }
  };

  private static onClipVolume = async (factor: number) => {
    if (tbSelection._clipSel) {
      tbSelection._clipFactor = (factor >= 0 ? factor : 1) / 10;
    }

    if (tbSelection._clipSel) {
      cViewClip.expandClipRange(tbSelection._clipFactor, tbSelection._range);
    }
  };

  private static showClipBox = () => {
    if (!tbSelection._clipBox) {
      cMessage.showStatusBarMessage(
        "Use handles to change clipping region",
        "icon-window-expand"
      );
      cViewClip.startClippingTool();
      tbSelection._clipBox = true;
    } else {
      cViewClip.stopClippingTool();
      tbSelection._clipBox = false;
      cMessage.clearStatusBarMessage();
    }

    tbSelection._clipSel = false;
    tbSelection._clipFactor = 0;
  };

  private static clearClip = () => {
    cViewClip.clearClips();
    tbSelection._clipBox = false;
    tbSelection._clipSel = false;
    tbSelection._clipFactor = 0;
  };

  // !!! AH
  /* 
    Mask is made for an element(s) on Button 
    Function cView.makeMask is called
  */
  private static createMask = () => {
    if (!this._maskTool) {
      this._maskTool = true;
      const elements = cSelection.getSelections();
      console.log("Elements Seleted: ", elements);
      // if (elements)
      //   cView.makeMask(elements);
      // else {
      //   cMessage.showStatusBarMessage("Please select at least one element");
      // }
      return;
    }
    else {
      this._maskTool = false;
      cMessage.clearStatusBarMessage();
      cView.removeMask();
      return;
    }
  };

  // private static isSelected = new ConditionalBooleanValue(() => {
  //   console.log("selection-state-changed");
  //   tbSelection._isSelected = !tbSelection._isSelected;
  //   return tbSelection._isSelected;
  //   }, ["selection-state-changed"]);

  //******************************************************************/
  // buttons - selection action toolbar
  //******************************************************************/
  public static provideToolbarButtonItems(): CommonToolbarItem[] {
    const labelText = new ConditionalStringValue(() => {
      return tbSelection._labelSel ? "Hide Selection Labels" : "Show Selection Labels";
    }, ["show-sel-label"]);

    const labelIcon = new ConditionalStringValue(() => {
      //      return tbSelection._labelSel ? "icon-info" : "icon-info-hollow";
      return tbSelection._labelSel ? "icon-visibility-hide-2" : "icon-label";
    }, ["show-sel-label"]);

    const selectionToolbar: CommonToolbarItem[] = [
      cFinder.popupFindButton(1000),
      ToolbarHelper.createToolbarItemFromItemDef(
        2000,
        CoreTools.selectElementCommand
      ),
      //        ToolbarHelper.createToolbarItemFromItemDef(2000, CoreTools.clearSelectionItemDef),
      ToolbarItemUtilities.createActionButton(
        "clearSelection",
        3000,
        "icon-selection-clear",
        "Clear Selection",
        tbSelection.clearSelection
      ),
      ToolbarItemUtilities.createActionButton(
        "labelSelection",
        4000,
        labelIcon,
        labelText,
        tbSelection.labelSelection
      ),
      ToolbarItemUtilities.createActionButton(
        "gotoSelection",
        5000,
        "icon-zoom-in-2",
        "Zoom To Selection",
        tbSelection.gotoSelection
      ),
      //        ToolbarItemUtilities.createActionButton("clipSelection",  5000, "icon-crop", "Clip Selection", tbSelection.clipSelection),
      cSlider.popupSliderButton(
        "clearClip",
        6000,
        "icon-crop",
        "Clip Selection",
        tbSelection.onClipVolume,
        tbSelection.onPopupClip,
        tbSelection.onCloseClip
      ),
      ToolbarItemUtilities.createActionButton(
        "clippingBox",
        7000,
        "icon-section-element",
        "Clip Box Selection",
        tbSelection.showClipBox
      ),
      ToolbarItemUtilities.createActionButton(
        "clearClipping",
        8000,
        "icon-refresh",
        "Clear Clipping",
        tbSelection.clearClip
      ),
      ToolbarItemUtilities.createActionButton(
        "createMask",
        9000,
        "icon-visibility-semi-transparent",
        "Make Mask",
        tbSelection.createMask
      )
    ];

    return selectionToolbar;
  }
}
//******************************************************************/

//******************************************************************/
// content tools toolbar - top left horizontal
//******************************************************************/
class tbTools {
  private static _showModel = false;
  private static _transparency = 0;
  private static _photoNav = false;
  private static _crossProbing = false;

  //******************************************************************/
  // callbacks - content tools toolbar
  //******************************************************************/
  private static clearMeasurement() {
    IModelApp.toolAdmin.currentTool?.onReinitialize();
  }

  private static clearSection() {
    IModelApp.tools.run(ViewClipClearTool.toolId);
  }

  private static showModel() {
    if (!tbTools._showModel) {
      cView.showCADModel();
      tbTools._showModel = true;
    }

    cMessage.showStatusBarMessage(
      "Use Slider to change transparency of reality model",
      "icon-visibility-semi-transparent"
    );
    cMessage.raiseUiEvent("show-cad-model");
    return tbTools._transparency * 100;
  }

  private static async hideModel() {
    cView.hideCADModel();

    cMessage.clearStatusBarMessage();
    tbTools._showModel = false;
    tbTools._transparency = 0;
    await cRealityData.setRealityDataTransparency(tbTools._transparency);
    cMessage.raiseUiEvent("show-cad-model");
  }

  private static onTransparency = async (transparency: number) => {
    tbTools._transparency = Math.abs(transparency / 100); // transparency is from 0 to 1
    await cRealityData.setRealityDataTransparency(tbTools._transparency);
  };

  private static photoNavigation() {
    if (!tbTools._photoNav) {
      cImage.startPhotoNavigation();
      tbTools._photoNav = true;
      cMessage.raiseUiEvent("show-photo-nav");
    } else {
      tbTools.endPhotoNavigation();
    }
  }

  private static endPhotoNavigation() {
    cImage.endPhotoNavigation();
    tbTools._photoNav = false;
    cMessage.raiseUiEvent("show-photo-nav");
  }

  private static setupWindowLayout(layout : string) {
    cMultiViewport.setupWindowLayout(layout);
    cCrossProbing.setupCrossProbing(false);
    if (layout == "2H" || layout == "3" || layout == "4") {
      cCrossProbing.setupCrossProbing(true);
    }
  }

  private static isClearMeasureHiddenCondition =
    new ConditionalBooleanValue((): boolean => {
      return !IModelApp.toolAdmin.currentTool?.toolId.startsWith("Measure.");
    }, [SyncUiEventId.ToolActivated]);

  //******************************************************************/
  // buttons - content tools toolbar
  //******************************************************************/
  public static provideToolbarButtonItems(): CommonToolbarItem[] {
    const cadVisibilityState = new ConditionalBooleanValue((): boolean => {
      return !tbTools._showModel;
    }, ["show-cad-model"]);

    const photoNavState = new ConditionalBooleanValue((): boolean => {
      return !tbTools._photoNav;
    }, ["show-photo-nav"]);

    const photoNavText = new ConditionalStringValue(() => {
      return tbTools._photoNav ? "Hide Camera Markers" : "Show Camera Markers";
    }, ["show-photo-nav"]);

    const photoNavIcon = new ConditionalStringValue(() => {
      return tbTools._photoNav ? "icon-visibility-hide-2" : "icon-camera";
    }, ["show-photo-nav"]);
    
    const viewItems: ActionButton[] = [
      ToolbarItemUtilities.createActionButton(
        "One",
        100,
        `svg:${iconOneWindow}`,
        "[1] - Reality Model",
        () => {tbTools.setupWindowLayout("1")}
      ),
      ToolbarItemUtilities.createActionButton(
        "TwoV",
        200,
        `svg:${iconTwoVWindow}`,
        "[2] - CAD Model",
        () => {tbTools.setupWindowLayout("2V")}
      ),
      ToolbarItemUtilities.createActionButton(
        "TwoH",
        250,
        `svg:${iconTwoHWindow}`,
        "[2] - Schematic",
        () => {tbTools.setupWindowLayout("2H")}
      ),
      ToolbarItemUtilities.createActionButton(
        "Three",
        300,
        `svg:${iconThreeWindow}`,
        "[3] - CAD + Schematic",
        () => {tbTools.setupWindowLayout("3")}
      ),
      ToolbarItemUtilities.createActionButton(
        "Four",
        400,
        `svg:${iconFourWindow}`,
        "[4] - CAD + Schematic",
        () => {tbTools.setupWindowLayout("4")}
      ),
    ];

    const toolsToolbar: CommonToolbarItem[] = [
      ToolbarHelper.createToolbarItemFromItemDef(
        1000,
        CoreTools.measureToolGroup
      ),
      ToolbarItemUtilities.createActionButton(
        "clearMeasure",
        2000,
        "icon-measure-clear",
        "Clear Measurements",
        tbTools.clearMeasurement
      ),
      //            , { isHidden: cToolbars.isClearMeasureHiddenCondition }
      //            , { isDisabled: true }),
      ToolbarHelper.createToolbarItemFromItemDef(
        3000,
        CoreTools.sectionToolGroupWithPanel
      ),
      ToolbarItemUtilities.createActionButton(
        "clearClip",
        4000,
        "icon-section-clear",
        "Clear Section",
        tbTools.clearSection
      ),
      //        ToolbarItemUtilities.createActionButton("showModel", 5000, "icon-panorama-model", "Show CAD Model", cToolbars.showModel),
      cSlider.popupSliderButton(
        "showModel",
        5000,
        "icon-panorama-model",
        "Show CAD Model",
        tbTools.onTransparency,
        tbTools.showModel
      ),
      ToolbarItemUtilities.createActionButton(
        "hideModel",
        6000,
        "icon-visibility-hide",
        "Hide CAD Model",
        tbTools.hideModel,
        { isHidden: cadVisibilityState }
      ),
      //      ToolbarItemUtilities.createActionButton("photoNav", 7000, "icon-camera", "Show Camera Markers", tbTools.photoNavigation),
      ToolbarItemUtilities.createActionButton(
        "photoNav",
        7000,
        photoNavIcon,
        photoNavText,
        tbTools.photoNavigation
      ),
      // ToolbarItemUtilities.createActionButton("endPhotoNav", 8000, "icon-visibility-hide-2", "Hide Camera Markers", tbTools.endPhotoNavigation,
      //                                         { isHidden: photoVisibilityState }),
      cRMSelect.popupRMSelectButton(8000),
      // ToolbarItemUtilities.createActionButton(
      //   "crossProbing",
      //   10000,
      //   "icon-sync",
      //   "Cross Probing",
      //   tbTools.crossProbing
      // )
      ToolbarItemUtilities.createGroupButton(
        "multiWindow",
        9000,
        "icon-window",
        "Multiple Windows",
        viewItems
      ),
    ];

    return toolsToolbar;
  }
}

//******************************************************************/
// content tools toolbar - top left vertical
//******************************************************************/
class tbHome {
  private static showHelp = () => {
    cHome.showHelp();
  };

  private static refresh = () => {
    cHome.refresh();
  };

  private static about = () => {
    cHome.aboutDialog();
  };

  private static logout = () => {
    cHome.logout();
  };

  //******************************************************************/
  // buttons - home corner item
  //******************************************************************/
  public static provideToolbarButtonItems(): CommonToolbarItem[] {
    const childItems: ActionButton[] = [
      ToolbarItemUtilities.createActionButton(
        "Help",
        100,
        "icon-help-hollow",
        "Geminai Help",
        tbHome.showHelp
      ),
      ToolbarItemUtilities.createActionButton(
        "Refresh",
        200,
        "icon-refresh",
        "Refresh",
        tbHome.refresh
      ),
      ToolbarItemUtilities.createActionButton(
        "About",
        300,
        `svg:${iconGeminai}`,
        "About Geminai",
        tbHome.about
      ),
      ToolbarItemUtilities.createActionButton(
        "Logout",
        400,
        "icon-exit",
        "Logout from Geminai",
        tbHome.logout
      ),
    ];

    const homeToolbar: CommonToolbarItem[] = [
      //        ToolbarItemUtilities.createActionButton("home", 100, `svg:${iconGeminai}`, "Clear Selection",  () => {}),
      ToolbarItemUtilities.createGroupButton(
        "home",
        100,
//        "icon-home",
        `svg:${iconGeminai}`,
        "GEMINAI",
        childItems
      ),
    ];

    return homeToolbar;
  }
}

//******************************************************************/

// From @bsteinbk, Issue# 2117
// if (toolbarUsage === ToolbarUsage.ContentManipulation && toolbarOrientation === ToolbarOrientation.Horizontal) {
//   const isClearMeasureHiddenCondition = new ConditionalBooleanValue(
//        (): boolean => !IModelApp.toolAdmin.currentTool?.toolId.startsWith("Measure."),
//        [SyncUiEventId.ToolActivated]);
//   const clearMeasureActionSpec = ToolbarItemUtilities.createActionButton("clear-measure-tool", -100, "icon-paintbrush", "Clear Measure Decorations",
//        (): void => { IModelApp.toolAdmin.currentTool?.onReinitialize(); }, { isHidden: isClearMeasureHiddenCondition });
//   return [clearMeasureActionSpec] }

// From @bsteinbk, Issue# 2177
// const conditionalIcon = new ConditionalStringValue(() => {
//   return myToggleManager.isActive ? "icon-camera-animation" : "icon-camera-animation-disabled";
// }, ["my-toggle-state-changed"]),

// const myToggleActionSpec = ToolbarItemUtilities.createActionButton("my-toggle-tool", -50,
//       conditionalIcon, "Toggle My Setting",
//       (): void => { myToggleManager.isActive = !myToggleManager.isActive;
//               // tell UI to refresh items registered to monitor my event
//               SyncUiEventDispatcher.dispatchSyncUiEvent("my-toggle-state-changed");
//       });
